<template>
  <span class="progress-bar-with-button" :class="{ squished: isSquished }">
    <ThreeDButton
      v-if="isSquished"
      class="progress-bar-button"
      :tabindex="tabindex"
      :disabled="disabled"
      @click="$emit('click', $event)"
    >
      <span
        role="progressbar"
        class="progress-bar-fill"
        :aria-valuenow="value"
        :aria-valuemin="min"
        :aria-valuemax="max"
        :style="progressBarFillStyle"
      >
        <FaIcon v-if="icon" :icon="icon" class="progress-bar-icon" />
        <slot />
      </span>
      <FaIcon v-if="icon" :icon="icon" class="progress-bar-icon" />
      <slot />
    </ThreeDButton>
    <template v-else>
      <span
        role="progressbar"
        class="progress-bar"
        :aria-valuenow="value"
        :aria-valuemin="min"
        :aria-valuemax="max"
        ><span class="progress-bar-fill" :style="progressBarFillStyle"></span>
      </span>
      <ThreeDButton
        class="progress-bar-button"
        :icon="icon"
        :tabindex="tabindex"
        :disabled="disabled"
        @click="$emit('click', $event)"
      >
        <slot />
      </ThreeDButton>
    </template>
  </span>
</template>

<script>
import ThreeDButton from './ThreeDButton'
import FaIcon from '@/components/ui/FaIcon'

export default {
  components: { FaIcon, ThreeDButton },
  props: {
    min: { type: Number, default: 0 },
    max: { type: Number, required: true },
    value: { type: Number, required: true },
    tabindex: [String, Number],
    disabled: Boolean,
    icon: { type: String },
  },
  data() {
    return { isSquished: false }
  },
  computed: {
    progressBarFillWidth() {
      return ((this.value - this.min) / (this.max - this.min)) * 100
    },
    progressBarFillStyle() {
      const path = `inset(0 ${100 - this.progressBarFillWidth}% 0 0)`

      return `clip-path: ${path}; -webkit-clip-path: ${path};`
    },
  },
  methods: {
    viewportObserver() {
      this.isSquished = window.innerWidth <= 425
    },
  },
  mounted() {
    this.viewportObserver()
    window.addEventListener('resize', this.viewportObserver)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.viewportObserver)
  },
}
</script>

<style lang="scss" scoped>
$progressColor: #8d8d8d;

.progress-bar-with-button {
  box-sizing: border-box;
  display: flex;
  align-items: center;

  .progress-bar {
    position: relative;
    display: none; // Hide the progress bar in browsers with no support for clip-path
    flex-grow: 1;
    z-index: 1;
    background: $white;
    border-radius: 2em;
    overflow: hidden;
  }

  .progress-bar-fill {
    position: absolute;
    display: block;
    overflow: hidden;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    line-height: 2.5em;
    background: $progressColor;
    color: $white;
    transition: clip-path 0.5s ease-out;
  }

  &:not(.squished) {
    .progress-bar {
      margin-right: 1em;
    }
  }

  &.squished {
    .progress-bar-button {
      --button-color: #{$white};
      --button-shadow-color: #{mix($progressColor, $white)};

      flex-grow: 1;
      font-size: 1.25em;
      color: $textColor !important;
      border-radius: 2em;
    }
  }

  @supports (clip-path: inset(0 10% 0 0)) {
    align-items: stretch;

    &:not(.squished) {
      .progress-bar {
        display: block;
      }
    }
  }
}

.progress-bar-icon {
  margin-right: 0.3em;
}
</style>
